import { inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";

import { AppStore } from "../../stores/app-store.service";
import { IncidentControllerService } from "../../services/incident-controller.service";
import { FilterUrlSyncService } from "../../services/filter-url-sync.service";
import { MAP_LIST_FILTER_CONFIG } from "../../services/filter-sync-configs";
import { FilterKey } from "../../services/filters.service";
import { CurrentStateDTO } from "../../models/current-state";
import { FilterStatus } from "../../models/filters";
import { IncidentDTO } from "../../models/incident";
import { Range } from "../../models/range-status-days";
import {
  readColorStatusSelection,
  writeColorStatusSelection
} from "../../models/color-status-filter";

/**
 * Which filter a page is being told about. The page reacts per filter — the list
 * feeds its export a different key for each — without this module knowing what
 * the reaction is.
 */
export type FleetFilter =
  | "containers"
  | "devices"
  | "incidentDevices"
  | "incidents"
  | "lastSites"
  | "statusDays"
  | "confidenceRadius"
  | "devicesTypes"
  | "containerCategory"
  | "torn"
  | "unknown"
  | "colorStatus";

/**
 * MAP and LIST are one fleet seen two ways. Both take the same eleven filters
 * from the same URL params, mirror them back to the same place, and answer the
 * same header and the same sidenav — they differ only in what they draw with the
 * result. That half was written twice, once per page, so every filter added to
 * this branch had to be added in both, and `mirrorFilters` / `shownStatusMarkers`
 * / `handleDeviceIncident` / `onChangeTab` ended up byte-identical copies.
 *
 * It lives here instead, as the base both pages extend rather than a service they
 * hold: the two headers and the sidenav bind these members by name from the page
 * templates, so a page has to keep answering to those names itself.
 *
 * A page fills in two things:
 *  - `refreshAfterFilterChange(filter)` — there is something new to fetch.
 *  - `onFilterSet(filter)` — the selection changed, whether or not that means a
 *    fetch. Optional; only the map needs it.
 *
 * What a page owns stays in the page: its request path, its rendering, its
 * progress, its record routes. `listSites`, `loadingProgress`, `requestId`,
 * `timeoutId` and `siteRequest` are named the same on both pages but are only ever
 * written by those per-page paths — declaring them here would move the duplication
 * rather than remove it, and buy coupling for nothing.
 */
export abstract class TrackerFleetFilters {
  protected readonly store = inject(AppStore);
  protected readonly incidentServices = inject(IncidentControllerService);
  protected readonly filterSync = inject(FilterUrlSyncService);
  protected readonly route = inject(ActivatedRoute);
  protected readonly router = inject(Router);

  protected subscription: Subscription = new Subscription();

  public filterContainers: string[] = [];
  public filterDevices: string[] = [];
  public filterDevicesTypes: string[] = [];
  public filterContainerCategory: string[] = [];
  public filterLastSites: string[] = [];
  public filterIncidents: FilterKey[] = [];
  public filterIncidentDevices: string[] = [];
  public filterConfidenceRadius: string[] = [];
  public filterStatusDays: Range = {};
  public filterTorn: string;
  public filterUnknown: string;
  /**
   * The open site's code. Not a URL-synced filter like its neighbours above: it is
   * the `/map/site/:site_code` path segment, and the page's site RecordRoute is the
   * only writer.
   */
  public filterSiteCode: string | undefined;
  public statusList: Array<any> = [];

  public showCreateIncident: boolean = false;
  public listIncidentDevices: CurrentStateDTO[] = [];
  public isEmptyIncidentDevice: boolean = true;

  public isSidenavOpen: boolean = false;

  // colorStatus (`status`) shown-markers hydrated from the URL. `[]` is a real
  // selection of nothing (= no colour filter); `undefined` means the URL carried
  // no `status` at all, and the selection restored from localStorage stands.
  private hydratedColorStatus: string[] | undefined;
  // Skip mirroring the first statusStore$ emission (the seed): the URL should
  // only reflect user-driven colour changes.
  private colorMirrorArmed: boolean = false;

  /** There is something new to fetch and draw. */
  protected abstract refreshAfterFilterChange(filter: FleetFilter): void;

  /**
   * The selection changed — called for every filter a user picks, including the
   * ones whose change means nothing to re-fetch.
   */
  protected onFilterSet(_filter: FleetFilter): void {}

  /**
   * Take every filter from the URL. The page calls this from its constructor
   * body rather than having it happen here, because a hydrated incident
   * selection resolves its devices straight away and so can reach
   * `refreshAfterFilterChange` before the page's own fields exist.
   *
   * The URL wins over localStorage (a shared link has to render what it says):
   * hydrate first, then take every filter field from the decoded values.
   * hydrateFromUrl has already written them to localStorage, so re-reading that
   * here would only decode the same thing a second time.
   */
  protected hydrateFilters(): void {
    const hydrated = this.filterSync.hydrateFromUrl(
      this.route,
      MAP_LIST_FILTER_CONFIG
    );
    this.filterContainers = hydrated["pkg"];
    this.filterDevices = hydrated["trk"];
    this.filterLastSites = hydrated["lastsite"];
    // A hydrated selection is text and a sidenav-driven one carries real numbers,
    // which is exactly what FilterKey is for — no conversion in either direction.
    this.filterIncidents = hydrated["inc"];
    this.filterStatusDays = hydrated["days"];
    this.filterConfidenceRadius = hydrated["rad"];
    this.filterDevicesTypes = hydrated["trktyp"];
    this.filterContainerCategory = hydrated["pkgcat"];
    // The two radios always have a value: the config declares their base one, so
    // the decoded value is never empty and never needs a fallback here.
    this.filterTorn = hydrated["torn"];
    this.filterUnknown = hydrated["unk"];
    // `status` is signal-backed, so seedColorStatus() applies it to the list the
    // store is seeded with.
    this.hydratedColorStatus = hydrated["status"];

    if (this.filterIncidents.length) {
      this.setIncidentDevicesId();
    }
  }

  /**
   * Seed the colour filter. Restore via readColorStatusSelection so the default
   * (all unchecked) and the legacy all-checked → unchecked migration are applied
   * consistently. Seed the store too: on a page refresh the in-memory store is
   * empty and the dashboard — its only other seeder — is not reloaded, so without
   * this the header's colorStatus badge and checkboxes would not reflect the
   * selection.
   *
   * `status` is the whole colour selection, not an override on what was just
   * restored: when the URL carries it, mark exactly the markers it lists as shown
   * (`status=` → none, the "no colour filter" state). `undefined` means the URL
   * said nothing about it — a tab detour through a page with no colour filter —
   * so the restored selection is kept as it is.
   *
   * Each page calls this where it seeds today, because only the page knows when
   * that is relative to its own mirror.
   */
  protected seedColorStatus(): void {
    const restored = readColorStatusSelection();

    if (this.hydratedColorStatus !== undefined) {
      const shown = new Set(this.hydratedColorStatus);
      restored.forEach((status) => (status.show = shown.has(status.marker)));
    }

    // Adopt it here as well as through the store: the page subscribes only after
    // its mirror, which reads the selection off statusList.
    this.statusList = restored;
    this.store.setStatus(restored);
  }

  /**
   * Publish the page's WHOLE filter set to the URL and to localStorage, empty ones
   * included (`?pkg=&trk=&days=3-10&…`): the address bar is the editable form of
   * what is displayed, so every filter has to be visible in it as a key to type
   * into. The service reads nothing back, so this has to list every param
   * MAP_LIST_FILTER_CONFIG declares — and it is the only thing that writes them.
   */
  protected mirrorFilters(): void {
    this.filterSync.mirrorToUrl(this.route, MAP_LIST_FILTER_CONFIG, {
      pkg: this.filterContainers,
      trk: this.filterDevices,
      lastsite: this.filterLastSites,
      inc: this.filterIncidents,
      days: this.filterStatusDays,
      rad: this.filterConfidenceRadius,
      trktyp: this.filterDevicesTypes,
      pkgcat: this.filterContainerCategory,
      torn: this.filterTorn,
      unk: this.filterUnknown,
      status: this.shownStatusMarkers()
    });
  }

  /** The colour filter's current selection: the markers `statusList` shows. */
  private shownStatusMarkers(): string[] {
    return (this.statusList ?? [])
      .filter((status) => status.show)
      .map((status) => status.marker);
  }

  /**
   * Follow the colour store. It is the one filter that does not arrive through a
   * change handler: the header and the sidenav write it into NgRx and the page
   * reads it back out. Each page calls this from its own store setup, because
   * only the page knows when its subscriptions may be made — the map guards
   * against re-subscribing when its container is re-created.
   */
  protected followColorStatusStore(): void {
    this.subscription.add(
      this.store.statusStore$.subscribe((statusData: Array<any>) => {
        this.statusList = statusData;
        writeColorStatusSelection(statusData);
        // Mirror the shown markers to the URL, but skip the initial seed emission
        // so a localStorage-only selection isn't written on load — only
        // user-driven colour changes update it. statusList is already the emitted
        // list, so mirrorFilters() picks the new selection up.
        //
        // Gated on a populated list for a different reason than the writer's:
        // the store's cleared value (undefined or []) is replayed to the first
        // subscriber after the next login, and it must neither publish an empty
        // status= nor consume the arming — which would make the real seed list
        // that arrives next look like a user-driven change.
        if (Array.isArray(statusData) && statusData.length) {
          if (this.colorMirrorArmed) {
            this.mirrorFilters();
          } else {
            this.colorMirrorArmed = true;
          }
        }
        this.refreshAfterFilterChange("colorStatus");
      })
    );
  }

  public changeContainers(containers: string[]): void {
    this.filterContainers = containers;
    this.applyFilterChange("containers");
  }

  public changeLastSites(sites: string[]): void {
    this.filterLastSites = sites;
    this.applyFilterChange("lastSites");
  }

  public changeConfidenceRadius(radius: string[]): void {
    this.filterConfidenceRadius = radius;
    this.applyFilterChange("confidenceRadius");
  }

  public changeDevicesTypes(devicesTypes: string[]): void {
    this.filterDevicesTypes = devicesTypes;
    this.applyFilterChange("devicesTypes");
  }

  public changeContainerCategory(categories: string[]): void {
    this.filterContainerCategory = categories;
    this.applyFilterChange("containerCategory");
  }

  public changeTorn(torn: string): void {
    this.filterTorn = torn;
    this.applyFilterChange("torn");
  }

  public changeUnknown(unknown: string): void {
    this.filterUnknown = unknown;
    this.applyFilterChange("unknown");
  }

  public changeStatusDays(statusDays: Range | undefined): void {
    // Reject a bound that is genuinely NaN, but NOT one that is simply absent:
    // `isNaN(undefined)` is true, which discarded every half-open range (a normal
    // use) and wrote an empty `days=` to the URL. Number.isNaN does not coerce, so
    // `{ start: 2 }` survives — and `undefined`, which clearAllFilters() emits,
    // no longer throws on the property read.
    if (Number.isNaN(statusDays?.start) || Number.isNaN(statusDays?.end)) {
      statusDays = undefined;
    }

    this.filterStatusDays = statusDays;
    this.applyFilterChange("statusDays");
  }

  public changeDevices(devices: string[]): void {
    this.filterDevices = devices;
    this.mirrorFilters();
    this.onFilterSet("devices");

    // An incident selection pins its own device list, so while one is active a
    // change here changes nothing to fetch.
    if (!this.filterIncidentDevices?.length) {
      this.refreshAfterFilterChange("devices");
    }
  }

  public changeIncidents(incidentIds: FilterKey[]): void {
    this.filterIncidents = incidentIds;
    this.mirrorFilters();
    this.onFilterSet("incidents");

    if (incidentIds?.length) {
      this.setIncidentDevicesId();
    } else {
      this.filterIncidentDevices = [];
      // Nothing pins the device list any more, so the fleet falls back to the
      // device filter — which, to a page, is the `devices` change.
      this.refreshAfterFilterChange("devices");
    }
  }

  /** Resolve the devices the selected incidents are on. */
  protected setIncidentDevicesId(): void {
    this.subscription.add(
      this.incidentServices
        .getDevicesIds(this.filterIncidents)
        .subscribe((devicesIds) => {
          this.filterIncidentDevices = devicesIds;
          this.refreshAfterFilterChange("incidentDevices");
        })
    );
  }

  /** Mirror the new value, then let the page react to it. */
  private applyFilterChange(filter: FleetFilter): void {
    this.mirrorFilters();
    this.onFilterSet(filter);
    this.refreshAfterFilterChange(filter);
  }

  /** Toggle one colour's visibility. The store emission does the rest. */
  public updateFilter(filter: any): void {
    this.onFilterSet("colorStatus");
    this.store.updateStatus(filter);
  }

  /** Replace the whole colour selection. The store emission does the rest. */
  public changeColorStatus(colors: FilterStatus[]): void {
    this.store.setStatus(colors);
  }

  public toggleSidenav(isOpen: boolean): void {
    this.isSidenavOpen = isOpen;
  }

  /**
   * Cross-tab jump to an incident: deep-links to /incident/:incident_id, which
   * fetches the incident by its SQL id.
   * @param info {any} Navigation payload carrying the clicked incident.
   */
  public onChangeTab(info: any): void {
    // Bare navigate on purpose: INCIDENT_FILTER_CONFIG is empty, so the incident
    // page declares no query params and there is nothing to carry over.
    if (info?.incident?.id !== undefined) {
      this.router.navigate(["/incident", info.incident.id]);
    }
  }

  /**
   * Triggered when the devices selected for a new incident change.
   * @param incidentIds {any} List of incident IDs, or a signal to open creation.
   */
  public changeIncidentDevices(incidentIds): void {
    if (!incidentIds) {
      this.showCreateIncident = true;
    }
  }

  public closeIncidentCreation(): void {
    this.showCreateIncident = false;
  }

  /**
   * Manages the checkbox selection of a device for incident creation.
   * @param deviceData {any} Device data with selection state.
   */
  public handleDeviceIncident(deviceData): void {
    const index = this.listIncidentDevices.findIndex(
      (device) => device.deviceId === deviceData.device.deviceId
    );

    if (deviceData.checked && index === -1) {
      this.listIncidentDevices = [
        ...this.listIncidentDevices,
        deviceData.device
      ];
    } else if (!deviceData.checked && index >= 0) {
      this.listIncidentDevices.splice(index, 1);
    }

    this.isEmptyIncidentDevice = this.listIncidentDevices.length === 0;
  }

  public addIncidentList(incident: IncidentDTO): void {
    console.log("incident Created: ", incident);
  }
}
